import * as THREE from 'three';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';

import defaultSkinTone from './defaultSkinTone';
import type { MorphIdentity, SceneMeshData } from './types';

function getMeshMaterial(mesh: THREE.Mesh): THREE.Material | undefined {
  const m = mesh.material;
  if (!m) return undefined;
  return Array.isArray(m) ? m[0] : m;
}

function getMeshMaterials(mesh: THREE.Mesh): THREE.Material[] {
  const material = mesh.material;
  if (!material) return [];
  const materials = Array.isArray(material) ? material : [material];
  return materials.filter(isThreeMaterial);
}

function isThreeMaterial(value: unknown): value is THREE.Material {
  return (
    value instanceof THREE.Material ||
    Boolean((value as { isMaterial?: boolean } | undefined)?.isMaterial)
  );
}

const LEGACY_SKIN_MATERIAL_NAMES = new Set(['Body', 'Face']);
const MHR_RANDOM_COLOR_USER_DATA_KEY = 'mhrRandomCharacterHex';
const MHR_MATERIAL_COLOR_VARIANTS = {
  body: { saturation: 0.48, lightness: 0.68 },
  shirt: { saturation: 0.74, lightness: 0.48 },
  pants: { saturation: 0.78, lightness: 0.32 },
} as const satisfies Record<string, { saturation: number; lightness: number }>;
type MhrMaterialName = keyof typeof MHR_MATERIAL_COLOR_VARIANTS;
type MhrHslColor = { h: number; s: number; l: number };
type MhrActorPalette = Record<MhrMaterialName, MhrHslColor>;
const MHR_ACTOR_PALETTE = [
  {
    body: { h: 0.035, s: 0.48, l: 0.68 },
    shirt: { h: 0.035, s: 0.76, l: 0.48 },
    pants: { h: 0.02, s: 0.74, l: 0.28 },
  },
  {
    body: { h: 0.16, s: 0.5, l: 0.68 },
    shirt: { h: 0.16, s: 0.85, l: 0.5 },
    pants: { h: 0.15, s: 0.72, l: 0.32 },
  },
  {
    body: { h: 0.49, s: 0.48, l: 0.68 },
    shirt: { h: 0.49, s: 0.78, l: 0.45 },
    pants: { h: 0.5, s: 0.74, l: 0.28 },
  },
  {
    body: { h: 0.31, s: 0.52, l: 0.68 },
    shirt: { h: 0.31, s: 0.82, l: 0.48 },
    pants: { h: 0.34, s: 0.8, l: 0.3 },
  },
  {
    body: { h: 0.71, s: 0.5, l: 0.72 },
    shirt: { h: 0.71, s: 0.82, l: 0.52 },
    pants: { h: 0.71, s: 0.82, l: 0.34 },
  },
  {
    body: { h: 0.58, s: 0.1, l: 0.7 },
    shirt: { h: 0.58, s: 0.08, l: 0.48 },
    pants: { h: 0.6, s: 0.1, l: 0.18 },
  },
  {
    body: { h: 0.58, s: 0.04, l: 0.78 },
    shirt: { h: 0.58, s: 0.04, l: 0.58 },
    pants: { h: 0.58, s: 0.05, l: 0.36 },
  },
] as const satisfies MhrActorPalette[];

export function hideMeshMaterials(meshData: SceneMeshData) {
  meshData.scene.traverse((child) => {
    if (child instanceof THREE.Mesh && child.type === 'SkinnedMesh') {
      const material = getMeshMaterial(child);
      if (material?.name?.includes('Hide')) {
        material.visible = false;
      }
      if (material?.isMaterial && child.geometry?.type === 'BufferGeometry') {
        // TODO: Subdivision modifier would go here. ThreeJS has poor support for it though
        // Tried to use https://github.com/stevinz/three-subdivide and it seemed to not work well for
        // modifying an existing mesh
      }
    }
    if ((child.name ?? '').endsWith('_RND')) {
      child.visible = false;
    }
  });
}

export function turnOnShadows(meshData: SceneMeshData) {
  meshData.scene.traverse((object: THREE.Object3D) => {
    object.castShadow = true;
    object.receiveShadow = true;
  });
}

export function disableFrustumCulled(meshData: SceneMeshData) {
  // Disable frustumCulled for children meant to be seen.
  // https://discourse.threejs.org/t/parts-of-glb-object-disappear-in-certain-angles-and-zoom/21295/4
  meshData.scene.traverse((child) => {
    if ('frustumCulled' in child) {
      const material =
        child instanceof THREE.Mesh ? getMeshMaterial(child) : undefined;
      if (child instanceof THREE.Mesh && material?.name?.includes('Hide')) {
        child.visible = false;
      } else {
        child.frustumCulled = false;
      }
    }
  });
}

export function showSkeleton(meshData: SceneMeshData) {
  const rootBone = meshData.scene.getObjectByName('root');
  if (rootBone) {
    const skeletonHelper = new THREE.SkeletonHelper(rootBone);
    (skeletonHelper.material as THREE.LineBasicMaterial).color =
      new THREE.Color(0x000000);
    meshData.scene.add(skeletonHelper);
  } else {
    console.warn('Could not find the root bone to show skeleton of');
  }
}

export function applySkinHex(
  skinHex?: string,
  charUploadID?: string,
  actorColorKey?: string,
) {
  return (meshData: SceneMeshData) => {
    if (!meshData) return;
    const isMhr = isMhrScene(meshData.scene);
    const hexString = resolveCharacterColorHex(meshData, skinHex, charUploadID);
    const baseColor = new THREE.Color(hexString);
    const hexNumber = baseColor.getHex();
    const mhrPalette = resolveMhrActorPalette(
      skinHex,
      actorColorKey,
      baseColor,
    );

    meshData.scene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.isMesh) {
        for (const material of getMeshMaterials(child)) {
          const materialName = material.name;
          if (isMhr) {
            applyMhrMaterialColor(material, mhrPalette, materialName);
            continue;
          }

          if (LEGACY_SKIN_MATERIAL_NAMES.has(materialName)) {
            applyMaterialColor(material, hexNumber);
          }
        }
      }
    });
  };
}

export function applyFaceExpression(faceExpression: string) {
  return (meshData: SceneMeshData) => {
    if (!meshData) return;
    const texturePath = `/images/static_faces_textures/${faceExpression}.png`;
    meshData.scene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.isMesh) {
        const material = getMeshMaterial(child);
        if (material?.name === 'Face') {
          // Axel doesn't have face expressions
          if (child.parent?.name?.includes('Axel')) {
            return;
          }
          new THREE.TextureLoader().load(texturePath, (tex) => {
            tex.flipY = false;
            (material as THREE.MeshStandardMaterial).map = tex;
            material.needsUpdate = true;
          });
        }
      }
    });
  };
}

export function applyShowSkeleton(
  meshData: SceneMeshData,
  showSkeleton: boolean,
) {
  if (!meshData) return;
  meshData.scene.traverse((child) => {
    const name = child.name ?? '';
    if (name.endsWith('_geo')) {
      child.visible = !showSkeleton;
    }
    if (name.endsWith('_skl')) {
      child.visible = showSkeleton;
    }
  });
}

export function applyShowRenderingCharacter(
  meshData: SceneMeshData,
  showRendering: boolean,
) {
  if (!meshData) return;
  meshData.scene.traverse((child) => {
    const name = child.name ?? '';
    if (name.endsWith('_geo')) {
      child.visible = !showRendering;
    }
    if (name.endsWith('_skl')) {
      child.visible = !showRendering;
    }
    if (name.endsWith('_RND')) {
      child.visible = showRendering;
    }
  });
}

function resolveMhrActorPalette(
  skinHex: string | undefined,
  actorColorKey: string | undefined,
  baseColor: THREE.Color,
) {
  if (skinHex && skinHex !== 'default') {
    return resolveMhrPaletteFromBaseColor(baseColor);
  }

  if (actorColorKey) {
    const paletteIndex = resolveMhrActorPaletteIndex(actorColorKey);
    return (
      MHR_ACTOR_PALETTE[paletteIndex % MHR_ACTOR_PALETTE.length] ??
      MHR_ACTOR_PALETTE[0]
    );
  }

  return resolveMhrPaletteFromBaseColor(baseColor);
}

function resolveMhrPaletteFromBaseColor(baseColor: THREE.Color) {
  const hsl = { h: 0, s: 0, l: 0 };
  baseColor.getHSL(hsl);

  return {
    body: {
      h: hsl.h,
      s: MHR_MATERIAL_COLOR_VARIANTS.body.saturation,
      l: MHR_MATERIAL_COLOR_VARIANTS.body.lightness,
    },
    shirt: {
      h: hsl.h,
      s: MHR_MATERIAL_COLOR_VARIANTS.shirt.saturation,
      l: MHR_MATERIAL_COLOR_VARIANTS.shirt.lightness,
    },
    pants: {
      h: hsl.h,
      s: MHR_MATERIAL_COLOR_VARIANTS.pants.saturation,
      l: MHR_MATERIAL_COLOR_VARIANTS.pants.lightness,
    },
  } satisfies MhrActorPalette;
}

function resolveMhrActorPaletteIndex(actorColorKey: string | undefined) {
  const key = actorColorKey ?? '';
  const sceneReferenceMatch = /^character(\d*)$/.exec(key);
  if (sceneReferenceMatch) {
    const rawIndex = sceneReferenceMatch[1];
    return rawIndex ? Math.max(0, Number(rawIndex) - 1) : 0;
  }

  const suffixMatch = /(?:^|[-_])(\d+)$/.exec(key);
  if (suffixMatch?.[1]) {
    return Number(suffixMatch[1]);
  }

  return 0;
}

function applyMhrMaterialColor(
  material: THREE.Material,
  palette: MhrActorPalette,
  materialName: string,
) {
  const color = palette[materialName as keyof typeof palette];
  if (!color) return;

  const colorMaterial = material as THREE.MeshStandardMaterial;
  if (!(colorMaterial.color instanceof THREE.Color)) return;

  colorMaterial.color.setHSL(color.h, color.s, color.l);
  colorMaterial.metalness = 0;
  colorMaterial.needsUpdate = true;
}

function applyMaterialColor(
  material: THREE.Material,
  hexNumber: number,
  shade = 1,
) {
  const colorMaterial = material as THREE.MeshStandardMaterial;
  if (!(colorMaterial.color instanceof THREE.Color)) return;

  colorMaterial.color.setHex(hexNumber).multiplyScalar(shade);
  colorMaterial.metalness = 0;
  colorMaterial.needsUpdate = true;
}

function resolveCharacterColorHex(
  meshData: SceneMeshData,
  skinHex: string | undefined,
  charUploadID: string | undefined,
) {
  if (isMhrScene(meshData.scene) && (!skinHex || skinHex === 'default')) {
    const userData = meshData.scene.userData as Record<string, unknown>;
    const existing = userData[MHR_RANDOM_COLOR_USER_DATA_KEY];
    if (typeof existing === 'string') {
      return existing;
    }

    const randomHex = randomMhrCharacterHex();
    userData[MHR_RANDOM_COLOR_USER_DATA_KEY] = randomHex;
    return randomHex;
  }

  return defaultSkinTone(skinHex, charUploadID);
}

function randomMhrCharacterHex() {
  return `#${new THREE.Color().setHSL(Math.random(), 0.72, 0.52).getHexString()}`;
}

// Detect mhr rig via the glb asset.extras.rig_type tag.
// See mhr-asset-extract/README.md "## frontend-monorepo usage".
export function detectMhr(gltfAssetExtras?: { rig_type?: string }): boolean {
  return gltfAssetExtras?.rig_type === 'MHR';
}

/**
 * After character mesh load, `userData.isMhr` is set from glTF `asset.extras.rig_type`.
 * MHR assets are already Y-up for BVH playback; skip the legacy -90° X correction used for older meshes.
 */
export function isMhrScene(scene: { userData?: unknown }): boolean {
  return Boolean((scene.userData as { isMhr?: boolean } | undefined)?.isMhr);
}

/**
 * True when a GLB self-declares Y-up via scene `extras.up` (surfaced on
 * `scene.userData`). Such meshes are already canonical and skip the legacy
 * -90° X correction applied to older Z-up meshes.
 */
export function sceneDeclaresYUp(scene: { userData?: unknown }): boolean {
  return (scene.userData as { up?: string } | undefined)?.up === 'Y';
}

/**
 * True when the GLB carries forge's `AxisCorrection` root node (robot GLBs from
 * `fbx_to_glb`). forge already reorients the robot to Y-up in the GLB, so the
 * player must skip the legacy -90° X correction — otherwise it double-rotates
 * the robot onto its side.
 */
export function sceneHasForgeAxisCorrection(scene: THREE.Object3D): boolean {
  return Boolean(scene.getObjectByName('AxisCorrection'));
}

// Set static morph target weights from identity.shapes on every SkinnedMesh in the scene that has a matching dictionary entry.
export function applyMorphShapes(
  meshData: SceneMeshData,
  identity: MorphIdentity,
): void {
  const shapes = identity?.shapes;
  if (!meshData || !shapes) return;
  meshData.scene.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.morphTargetInfluences || !mesh.morphTargetDictionary) return;
    for (const [name, weight] of Object.entries(shapes)) {
      const idx = mesh.morphTargetDictionary[name];
      if (idx !== undefined) mesh.morphTargetInfluences[idx] = weight;
    }
  });
}

// Apply identity.joint_scales as one-shot bone[prop][axis] writes; outer coeff key is informational and ignored at apply time.
export function applyIdentityJointScales(
  meshData: SceneMeshData,
  identity: MorphIdentity,
): void {
  if (!meshData || !identity?.joint_scales) return;
  for (const joints of Object.values(identity.joint_scales)) {
    for (const [name, props] of Object.entries(joints)) {
      const bone = meshData.scene.getObjectByName(name);
      if (!bone) continue;
      for (const [prop, axes] of Object.entries(props)) {
        const target = (
          bone as unknown as Record<string, Record<string, number>>
        )[prop];
        if (target) Object.assign(target, axes);
      }
    }
  }
}

export const MHR_BODY_WORLD_BONE_NAME = 'body_world';

/**
 * Apply once after joint_scales. Orbit's ratios include the final export's bone
 * matching, height/marker scaling and floor rebase. Remove the uniform height
 * component before changing local offsets; height is applied once on body_world
 * so the mesh and skeleton scale together. Keep the original skin bind matrices
 * so residual corrections deform the mesh with the rig.
 */
export function applyIdentityRestBoneScales(
  meshData: SceneMeshData,
  identity: MorphIdentity,
): boolean {
  let applied = false;
  const rawHeight = Number(identity.height_scale_factor);
  const heightFactor =
    Number.isFinite(rawHeight) && rawHeight > 0 ? rawHeight : 1;

  for (const [name, factor] of Object.entries(identity.rest_bone_scale ?? {})) {
    if (name === MHR_BODY_WORLD_BONE_NAME) continue;
    if (!Number.isFinite(factor) || factor <= 0) continue;

    const bone = meshData.scene.getObjectByName(name) as THREE.Bone | undefined;
    if (!bone?.isBone) continue;

    bone.position.multiplyScalar(factor / heightFactor);
    bone.updateMatrix();
    applied = true;
  }
  if (applied) updateIdentitySkeleton(meshData);
  return applied;
}

function updateIdentitySkeleton(meshData: SceneMeshData): void {
  meshData.scene.updateMatrixWorld(true);
  meshData.scene.traverse((child) => {
    const skinned = child as THREE.SkinnedMesh;
    if (skinned.isSkinnedMesh) skinned.skeleton.update();
  });
}

function resolveHeightScaleFactor(identity: MorphIdentity): number | null {
  const raw = identity.height_scale_factor;
  if (raw == null) return null;

  const factor = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(factor) || factor <= 0) return null;
  if (Math.abs(factor - 1) < 1e-6) return null;

  return factor;
}

/**
 * Applies identity.height_scale_factor as a uniform scale on the MHR root bone so
 * the full rig height matches scene/comic calibration without retargeting BVH.
 */
export function applyIdentityHeightScale(
  meshData: SceneMeshData,
  identity: MorphIdentity,
): void {
  if (!meshData) return;

  const factor = resolveHeightScaleFactor(identity);
  if (factor == null) return;

  const bodyWorld = meshData.scene.getObjectByName(MHR_BODY_WORLD_BONE_NAME);
  if (!bodyWorld) {
    console.warn(
      `[mhr] height_scale_factor=${factor} but bone "${MHR_BODY_WORLD_BONE_NAME}" was not found`,
    );
    return;
  }

  bodyWorld.scale.multiplyScalar(factor);
  bodyWorld.updateMatrix();
  updateIdentitySkeleton(meshData);

  const sceneUserData = meshData.scene.userData as Record<string, unknown>;
  sceneUserData.mhrHeightScaleFactor = factor;
}
